import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ApplicationState {
  private readonly authService = inject(MsalService);
  private readonly broadcastService = inject(MsalBroadcastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly dexmachTenantId = '99c75c69-9ef2-4c4e-b5e3-54b6cf95c977';

  // auth state
  readonly accounts = signal<AccountInfo[]>([]);
  readonly activeAccount = signal<AccountInfo | null>(null);
  readonly loginError = signal<string | null>(null);

  // derived auth state
  readonly isAuthenticated = computed(() =>
    this.activeAccount() !== null,
  );

  readonly roles = computed<string[]>(() => {
    const roles = this.activeAccount()?.idTokenClaims?.roles;
    return Array.isArray(roles) ? roles : [];
  });

  private readonly normalizedRoles = computed(() =>
    new Set(this.roles().map(role => role.toLowerCase())),
  );

  readonly isReader = computed(() => {
    const roles = this.normalizedRoles();

    return (
      roles.has('reader') ||
      roles.has('contributor') ||
      roles.has('owner')
    );
  });

  readonly isContributor = computed(() => {
    const roles = this.normalizedRoles();

    return (
      roles.has('contributor') ||
      roles.has('owner')
    );
  });

  readonly isOwner = computed(() =>
    this.normalizedRoles().has('owner'),
  );

  readonly isHealthGuardUser = computed(() => {
    const roles = this.normalizedRoles();

    return (
      roles.has('healthguard.readwrite') ||
      this.isContributor() ||
      this.isOwner()
    );
  });

  readonly username = computed(() =>
    this.activeAccount()?.username ?? null,
  );

  readonly tenantId = computed(() =>
    this.activeAccount()?.tenantId ?? null,
  );

  constructor() {
    this.initialize();
  }

  initialize(): void {
    this.broadcastService.inProgress$
      .pipe(
        filter(status => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.syncAuthenticationState();
      });
  }

  /**
   * Re-reads MSAL account state and selects the correct DexMach account.
   *
   * Kept public so callers that previously used loadUserData() or
   * checkAndSetActiveAccount() can still explicitly refresh auth state.
   */
  syncAuthenticationState(): AccountInfo | null {
    this.loginError.set(null);
    const allAccounts = this.authService.instance.getAllAccounts();
    const tenantAccounts = allAccounts.filter(account => account.tenantId === this.dexmachTenantId);
    this.accounts.set(tenantAccounts);
    if (!tenantAccounts.length) {
      this.activeAccount.set(null);
      if (allAccounts.length > 0) {
        this.loginError.set('No account found in DexMach tenant.');
      }
      return null;
    }
    const current = this.authService.instance.getActiveAccount();
    const activeAccount = current?.tenantId === this.dexmachTenantId ? current : tenantAccounts[0];

    this.activeAccount.set(activeAccount);
    this.authService.instance.setActiveAccount(activeAccount);
    return activeAccount;
  }

  /**
   * Compatibility with the old API.
   */
  checkAndSetActiveAccount(): AccountInfo | null {
    return this.syncAuthenticationState();
  }

  /**
   * Compatibility with the old API.
   *
   * This now refreshes authentication only.
   * Power BI data belongs to PowerBiState.
   */
  loadUserData(): void {
    this.syncAuthenticationState();
  }

  resetState(): void {
    this.accounts.set([]);
    this.activeAccount.set(null);
    this.loginError.set(null);
  }
}